import * as React from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Select from 'react-select';
import { v4 as uuid } from 'uuid';

import { CheckableDropdownOption } from './checkableDropdownOption';
import { CheckableDropdownValueContainer } from './checkableDropdownValueContainer';
import { SearchableCheckableDropdownMenuList } from './searchableCheckableDropdownMenuList';
import { HTMLDropdownOption } from './htmlDropdownOption';
import { HTMLDropdownSingleValue } from './htmlDropdownSingleValue';
import { StandardDropdownOption } from './standardDropdownOption';

import { TAB, ESCAPE, MOUSEDOWN, } from '../../index'

export interface Option {
  label: string,
  value: string | number,
  isDisabled?: boolean,
  [key: string]: any
}

interface DropdownInputProps {
  options: Array<Option>;
  className?: string;
  disabled?: boolean;
  error?: string;
  handleCancel?: (event: any) => void;
  handleChange?: (selection: any | any[]) => void;
  helperText?: string;
  label?: string | JSX.Element;
  ariaLabel?: string;
  id?: string;
  isMulti?: boolean;
  isSearchable?: boolean;
  onClick?: (event?: any) => void;
  optionType?: string;
  optionTypePlural?: string;
  optionTypeDescriptor?: string;
  placeholder?: string;
  timesSubmitted?: Number;
  type?: string;
  usesCustomOption?: boolean;
  value?: any;
  filterOptions?: (
    candidate: { label: string; value: string; data: any },
    value: string,
    options: { label: string; value: string }[]
  ) => void;
  handleInputChange?: any,
  allValue?: string,
  persistInputValue?: boolean;
  labelAdjacentElement?: JSX.Element;
  customMenuIconUrl?: string;
  maxVisibleOptions?: number;
  selectProps?: Partial<React.ComponentProps<typeof Select>>;
  showAllOption?: boolean;
}

const KEYDOWN = 'keydown'
const TOUCHSTART = 'touchstart'

export const DropdownInput: React.FC<DropdownInputProps> = ({
  options,
  className = '',
  disabled,
  error,
  handleCancel,
  handleChange,
  helperText,
  label,
  ariaLabel,
  id,
  isMulti = false,
  isSearchable = false,
  onClick,
  optionType,
  optionTypePlural,
  optionTypeDescriptor,
  placeholder,
  timesSubmitted,
  type,
  usesCustomOption,
  value,
  filterOptions,
  handleInputChange: propHandleInputChange,
  allValue,
  persistInputValue = false,
  labelAdjacentElement,
  customMenuIconUrl,
  maxVisibleOptions,
  selectProps: passedSelectProps,
  showAllOption = true,
}) => {
  const [active, setActive] = useState(false);
  const [errorAcknowledged, setErrorAcknowledged] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [computedOptions, setComputedOptions] = useState<Array<any>>([]);
  const [inputValue, setInputValue] = useState('');

  const inputRef = useRef<any>(null);
  const nodeRef = useRef<HTMLDivElement>(null);
  const idToUse = useMemo(() => id || uuid(), [id]);

  useEffect(() => {
    setErrorAcknowledged(false);
  }, [error, timesSubmitted]);

  const hasAllOption = isMulti && !!optionType && showAllOption

  const handleUpdatedOptions = useCallback((receivedOptions: any) => {
    const opts = receivedOptions ? receivedOptions : options;
    const allOption = hasAllOption ? { label: `All ${optionTypePlural || `${optionType}s`}`, value: 'All' } : null
    const passedOptions = allOption ? [allOption].concat(opts) : opts

    setComputedOptions(passedOptions);
  }, [options, hasAllOption, optionType, optionTypePlural]);

  useEffect(() => {
    handleUpdatedOptions(options);
  }, [options, handleUpdatedOptions]);

  const deactivateInput = useCallback(() => {
    setActive(false);
    setMenuIsOpen(false);
    setInputValue(prevValue => persistInputValue ? prevValue : '');
  }, [persistInputValue]);

  const handleClick = useCallback((e: MouseEvent | TouchEvent) => {
    const target = e.target as HTMLElement;
    if (!nodeRef.current || !nodeRef.current.contains(target)) {
      if (target.closest?.('.dropdown__menu-portal')) {
        return;
      }
      deactivateInput();
    }
  }, [deactivateInput]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    switch (e.key) {
      case ESCAPE:
      case TAB:
        deactivateInput();
        break;
      default:
        break;
    }
  }, [deactivateInput]);

  useEffect(() => {
    document.addEventListener(MOUSEDOWN, handleClick, true);
    document.addEventListener(TOUCHSTART, handleClick, true);
    document.addEventListener(KEYDOWN, handleKeyDown, true);

    return () => {
      document.removeEventListener(MOUSEDOWN, handleClick, true);
      document.removeEventListener(TOUCHSTART, handleClick, true);
      document.removeEventListener(KEYDOWN, handleKeyDown, true);
    };
  }, [handleClick, handleKeyDown]);

  const handleInputInteraction = (e: any) => {
    if (onClick) { onClick() }

    if (!disabled && (!menuIsOpen || !active)) {
      setActive(true);
      setMenuIsOpen(true);
    }
  };

  const filteredOptions = useMemo(() => {
    const normalizedInput = inputValue.trim().toLocaleLowerCase();
    const matches = normalizedInput.length
      ? computedOptions.filter(option => option.label.toLocaleLowerCase().includes(normalizedInput))
      : computedOptions;

    return maxVisibleOptions ? matches.slice(0, maxVisibleOptions) : matches;
  }, [computedOptions, inputValue, maxVisibleOptions]);

  const handleInputChangeInternal = (newInputValue: string, action: any) => {
    if (action.action !== 'input-change') {
      return
    }

    setInputValue(newInputValue);

    if (propHandleInputChange) {
      propHandleInputChange(newInputValue);
    }
  };

  const handleErrorAcknowledgement = () => {
    setErrorAcknowledged(true);
    setActive(true);
    setMenuIsOpen(true);
  };

  function renderHelperText() {
    if (helperText) {
      return <span className="helper-text">{helperText}</span>
    }
  }

  function renderErrorText() {
    if (error) {
      return <span className="error-text">{error}</span>
    }
  }

  const handleOptionSelection = (selection: any, meta: any = null) => {
    const allWasClicked = Array.isArray(selection) && selection.find((opt: any) => opt.value === 'All')

    if (allWasClicked) {
      if (value && value.length) {
        handleChange([])
      } else {
        handleChange(options)
      }
    } else {
      handleChange(selection)
    }

    if (!isMulti) { deactivateInput() }
  };

  const handleKeyDownOnInputContainer = (e: React.KeyboardEvent) => {
    if ([TAB, ESCAPE].includes(e.key)) { return }
    handleInputInteraction(e)
  };

  const handleFilterOptions = (candidate: { label: string; value: string; data: any }, value: string) => {
    return filterOptions(candidate, value, computedOptions)
  };

  const passedValue = value || ''
  const hasText = value || isMulti ? 'has-text' : ''
  const inactiveOrActive = active ? 'active' : 'inactive'
  const notEditable = isSearchable || isMulti ? '' : 'not-editable'
  const searchable = isSearchable ? 'searchable' : ''
  const checkboxDropdown = isMulti ? 'checkbox-dropdown' : ''
  const customIconClass = customMenuIconUrl ? 'has-custom-menu-icon' : ''
  const customIconStyle = customMenuIconUrl ? { '--custom-menu-icon-url': `url(${customMenuIconUrl})` } as React.CSSProperties : {}
  const noVisibleLabel = label ? '' : 'no-visible-label'
  const sharedClasses = `dropdown-container input-container ${inactiveOrActive} ${hasText} ${notEditable} ${className} ${searchable} ${checkboxDropdown} ${customIconClass} ${noVisibleLabel}`.replace(/\s+/g, ' ').trim()
  const labelId = `label-for-${idToUse}`
  const ariaLabelForLabel = label && !ariaLabel ? null : ariaLabel
  const labelElement = <label aria-label={ariaLabelForLabel} id={labelId}>{label}</label>
  const labelWrapper = labelAdjacentElement ? <span className="label-wrapper">{labelElement}{labelAdjacentElement}</span> : labelElement

  const allOptionsAreSelected = hasAllOption &&
    Array.isArray(passedValue) && passedValue.length === computedOptions.length - 1

  const sharedProps = {
    ...(label ? { 'aria-labelledby': labelId } : { 'aria-label': ariaLabel }),
    allOptionsAreSelected,
    className: "dropdown",
    classNamePrefix: "dropdown",
    components: { Option: StandardDropdownOption },
    filterOption: filterOptions ? handleFilterOptions : null,
    id: idToUse,
    inputValue,
    isClearable: false,
    isDisabled: disabled,
    isSearchable,
    onFocus: handleInputInteraction,
    onInputChange: handleInputChangeInternal,
    options: filteredOptions,
    placeholder: placeholder || '',
    ref: inputRef,
    tabSelectsValue: false,
    type,
    value: passedValue,
    ...passedSelectProps,
  }

  if (error) {
    if (errorAcknowledged) {
      return (
        <div
          className={`error ${sharedClasses}`}
          onClick={handleInputInteraction}
          onKeyDown={handleKeyDownOnInputContainer}
          onTouchStart={handleInputInteraction}
          ref={nodeRef}
          style={customIconStyle}
        >
          {labelWrapper}
          <Select
            {...sharedProps}
            menuIsOpen={menuIsOpen}
            onChange={handleOptionSelection}
          />
        </div>
      )
    } else {
      return (
        <div
          className={`error unacknowledged ${sharedClasses}`}
          onClick={handleErrorAcknowledgement}
          onKeyDown={handleErrorAcknowledgement}
          onTouchStart={handleErrorAcknowledgement}
          ref={nodeRef}
          style={customIconStyle}
        >
          {labelWrapper}
          <Select
            {...sharedProps}
            menuIsOpen={false}
          />
          {renderErrorText()}
        </div>
      )
    }
  } else if (isMulti) {
    if (isSearchable) {
      return (
        <div
          className={sharedClasses}
          onClick={handleInputInteraction}
          onKeyDown={handleKeyDownOnInputContainer}
          onTouchStart={handleInputInteraction}
          ref={nodeRef}
          style={customIconStyle}
        >
          {labelWrapper}
          <Select
            {...sharedProps}
            closeMenuOnSelect={false}
            components={{ Option: CheckableDropdownOption, ValueContainer: CheckableDropdownValueContainer, MenuList: SearchableCheckableDropdownMenuList, }}
            hideSelectedOptions={false}
            isMulti={true}
            isSearchable={false}
            menuIsOpen={active ? menuIsOpen : false}
            menuPlacement="auto"
            menuShouldScrollIntoView={false}
            onChange={handleOptionSelection}
            optionType={optionType}
            optionTypeDescriptor="selected"
            optionTypePlural={optionTypePlural}
          />
        </div>
      )
    } else {
      return (
        <div
          className={sharedClasses}
          onClick={handleInputInteraction}
          onKeyDown={handleKeyDownOnInputContainer}
          onTouchStart={handleInputInteraction}
          ref={nodeRef}
          style={customIconStyle}
        >
          {labelWrapper}
          <Select
            {...sharedProps}
            closeMenuOnSelect={false}
            components={{ Option: CheckableDropdownOption, ValueContainer: CheckableDropdownValueContainer }}
            hideSelectedOptions={false}
            isMulti
            menuIsOpen={active ? menuIsOpen : false}
            menuPlacement="auto"
            menuShouldScrollIntoView={false}
            onChange={handleOptionSelection}
            optionType={optionType}
            optionTypeDescriptor={optionTypeDescriptor}
            optionTypePlural={optionTypePlural}
          />
        </div>
      )
    }
  } else if (usesCustomOption) {
    return (
      <div
        className={sharedClasses}
        onClick={handleInputInteraction}
        onKeyDown={handleKeyDownOnInputContainer}
        onTouchStart={handleInputInteraction}
        ref={nodeRef}
        style={customIconStyle}
      >
        {labelWrapper}
        <Select
          {...sharedProps}
          components={{ SingleValue: HTMLDropdownSingleValue, Option: HTMLDropdownOption }}
          hideSelectedOptions={false}
          menuIsOpen={active ? menuIsOpen : false}
          onChange={handleOptionSelection}
        />
        {renderHelperText()}
      </div>
    )
  } else if (!active) {
    return (
      <div
        className={sharedClasses}
        onClick={handleInputInteraction}
        onKeyDown={handleKeyDownOnInputContainer}
        onTouchStart={handleInputInteraction}
        ref={nodeRef}
        style={customIconStyle}
      >
        {labelWrapper}
        <Select
          {...sharedProps}
          menuIsOpen={false}
        />
        {renderHelperText()}
      </div>
    )
  } else {
    return (
      <div
        className={`dropdown ${sharedClasses}`}
        onClick={handleInputInteraction}
        onKeyDown={handleKeyDownOnInputContainer}
        onTouchStart={handleInputInteraction}
        ref={nodeRef}
        style={customIconStyle}
      >
        {labelWrapper}
        <Select
          {...sharedProps}
          menuIsOpen={menuIsOpen}
          onChange={handleOptionSelection}
        />
      </div>
    )
  }
};
